import {QueryKey, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import api from "@/lib/api";
import {normalizePage} from "@/lib/paging.ts";
import {bannerKeys, BannerPage} from "@/hooks/useBanners.ts";

// ───────────────────────────────────────────────────────────────────────────────
// Types
// ───────────────────────────────────────────────────────────────────────────────
export type CompetitionId = string;

export type Competition = {
    id: CompetitionId;
    title: string;
    prizes?: string[];
    description: string;
    imageUrl?: string;
    targetUrl?: string;
    startAt: string | null;
    endAt: string | null;
    status: string;
    code?: string | null;
    entryFee?: number | null;
}

export type SaveCompetitionInput = {
    id?: CompetitionId;
    title: string;
    description: string;
    prizes: string[];
    file?: File;
    targetUrl?: string | null;
    startAt: string;
    endAt: string;
    status: string,
    code?: string | null;
    entryFee?: number | null;
    onUploadProgress?: (pct: number) => void;
};

export type SortField = "title" | "status" | "startAt" | "endAt";
export type SortOrder = "asc" | "desc";

export type HookParams = {
    page?: number;
    size?: number;
    sortBy?: SortField;
    sortOrder?: SortOrder;
};

export type CompetitionPage = ReturnType<typeof normalizePage<Competition>>;

// ───────────────────────────────────────────────────────────────────────────────
// Constants
// ───────────────────────────────────────────────────────────────────────────────
const PUBLIC_BASE = "/api/v1/public/competitions";
const ADMIN_BASE = "/api/v1/editor/competitions";

const DEFAULTS = {
    page: 0,
    size: 10,
    sortBy: "title" as SortField,
    sortOrder: "desc" as SortOrder,
};

// ───────────────────────────────────────────────────────────────────────────────
// Query Keys
// ───────────────────────────────────────────────────────────────────────────────
export const compKeys = {
    all: ["competitions"] as const,
    list: (params?: HookParams) => [...compKeys.all, "list", params ?? {}] as const,
};

// ───────────────────────────────────────────────────────────────────────────────
// Fetchers
// ───────────────────────────────────────────────────────────────────────────────
function emptyPage(page = DEFAULTS.page, size = DEFAULTS.size): CompetitionPage {
    return normalizePage<Competition>({page, size, total: 0, content: []});
}

async function fetchCompetitions(
    params: HookParams = {}
): Promise<CompetitionPage> {
    const page = params.page ?? DEFAULTS.page;
    const size = params.size ?? DEFAULTS.size;
    const sortBy = params.sortBy ?? DEFAULTS.sortBy;
    const sortOrder = params.sortOrder ?? DEFAULTS.sortOrder;

    const sort = `${sortBy},${sortOrder}`;
    const {data} = await api.get(PUBLIC_BASE, {params: {page, size, sort}});

    if (Array.isArray(data?.content)) return normalizePage<Competition>(data);
    if (Array.isArray(data?.items) && typeof data?.total === "number") return data;
    return emptyPage(page, size);
}

// ───────────────────────────────────────────────────────────────────────────────
// Queries
// ───────────────────────────────────────────────────────────────────────────────
export function useCompetitions(params?: HookParams) {
    const q: HookParams = {
        page: params?.page ?? DEFAULTS.page,
        size: params?.size ?? DEFAULTS.size,
        sortBy: params?.sortBy ?? DEFAULTS.sortBy,
        sortOrder: params?.sortOrder ?? DEFAULTS.sortOrder,
    };

    return useQuery({
        queryKey: compKeys.list(q),
        queryFn: () => fetchCompetitions(q),
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        placeholderData: (prev) => prev,
    });
}

// ───────────────────────────────────────────────────────────────────────────────
// Mutations
// ───────────────────────────────────────────────────────────────────────────────
export function useSaveCompetition() {
    const qc = useQueryClient();

    return useMutation({
        mutationKey: [...compKeys.all, "save"],
        mutationFn: async (payload: SaveCompetitionInput): Promise<Competition> => {
            const fd = new FormData();
            fd.append("title", payload.title.trim());
            fd.append("description", payload.description.trim());

            fd.append("startAt", payload.startAt);
            fd.append("endAt", payload.endAt);

            if (payload.code) fd.append("code", payload.code);
            if (payload.entryFee) fd.append("entryFee", payload.entryFee.toString());

            for (const p of payload.prizes.map((x) => x.trim()).filter(Boolean)) {
                fd.append("prizes", p);
            }

            if (payload.file) fd.append("file", payload.file);
            if (payload.targetUrl) fd.append("targetUrl", payload.targetUrl);
            fd.append("status", payload.status);

            const cfg: any = {headers: {"Content-Type": "multipart/form-data"}};

            if (payload.onUploadProgress) {
                cfg.onUploadProgress = (evt: ProgressEvent) => {
                    if (!evt.total) return;
                    payload.onUploadProgress!(Math.round((evt.loaded * 100) / evt.total));
                };
            }

            if (payload.id) {
                const {data} = await api.put(`${ADMIN_BASE}/${payload.id}`, fd, cfg);
                return data;
            } else {
                const {data} = await api.post(ADMIN_BASE, fd, cfg);
                return data;
            }
        },
        onSuccess: () => {
            qc.invalidateQueries({queryKey: compKeys.list()});
        },
    });
}

export function useDeleteCompetition() {
    const qc = useQueryClient();

    return useMutation({
        mutationKey: [...compKeys.all, "delete"],
        mutationFn: async (id: CompetitionId) => {
            await api.delete(`${ADMIN_BASE}/${id}`);
            return id;
        },
        onMutate: async (id) => {
            await qc.cancelQueries({queryKey: compKeys.all});

            const snapshots = qc.getQueriesData<unknown>({queryKey: compKeys.all});

            snapshots.forEach(([key, value]) => {
                const page = value as Partial<BannerPage> | undefined;
                if (page && Array.isArray(page.items)) {
                    const next: BannerPage = {
                        ...(page as BannerPage),
                        items: page.items.filter((b: any) => b.id !== id),
                        total: Math.max(0, (page.total as number) - 1),
                    };
                    qc.setQueryData(key, next);
                }
            });

            return {snapshots};
        },
        onError: (_err, _id, ctx) => {
            // Roll back from snapshots if we have them
            ctx?.snapshots?.forEach(([key, value]: [unknown, unknown]) => {
                qc.setQueryData(<QueryKey>key, value);
            });
        },
        onSettled: () => {
            // Ensure server truth wins in the end
            qc.invalidateQueries({queryKey: bannerKeys.all});
        },
    });
}